import { LuRefreshCcw } from "react-icons/lu";
import Modal from "../../modal/Modal";
import RadioButtonControlGroup from "../../shared/RadioButtonControlGroup";
import TopBar from "../common/TopBar";
import MainContent from "./children/MainContent";
import {
    IMPORT_MODAL_COPY,
    PLANS_TAB_OPTIONS,
    REFRESH_LABEL_BY_TAB,
    REFRESH_OUTCOME_STATUS,
    usePlans,
} from "./usePlans";
import { PlanPlatformTab } from "../common/constants";
import "./Plans.scss";

/**
 * Plans list screen — presentational shell delegating state to {@link usePlans}.
 *
 * @returns {import('react').JSX.Element}
 */
const Plans = () => {
    const {
        activeTab,
        searchCriteria,
        setSearchCriteria,
        selectedClientName,
        isImportOverwriteModalOpen,
        isPlanIdMismatchModalOpen,
        planIdMismatchDetails,
        importFileInputRef,
        visiblePlans,
        handleTabChange,
        handleRefresh,
        handleStartOrEditPlan,
        handleStartOrEditNotification,
        handleExportPlan,
        handleImportPlan,
        handleImportFileSelected,
        handleOverwriteFileSelected,
        handleOverwriteCancel,
        handlePlanIdMismatchConfirm,
        handlePlanIdMismatchCancel,
        handleSelectClient,
        isPlansLoading,
        isPlansError,
        plansErrorMessage,
        isRefreshInProgress,
        refreshProgress,
        refreshOutcome,
        handleCloseRefreshModal,
    } = usePlans();

    const plansForTable = isPlansLoading || isPlansError ? [] : visiblePlans;
    const refreshStatusText =
        refreshOutcome?.message ||
        refreshProgress.message ||
        refreshProgress.stage ||
        "Refreshing plan list…";
    const refreshTitle =
        refreshOutcome?.status === REFRESH_OUTCOME_STATUS.SUCCESS
            ? "Refresh complete"
            : refreshOutcome?.status === REFRESH_OUTCOME_STATUS.ERROR
              ? "Refresh failed"
              : "Refreshing plans";
    const refreshOutcomeClassName =
        refreshOutcome?.status === REFRESH_OUTCOME_STATUS.SUCCESS
            ? "plans-refresh-modal__text plans-refresh-modal__text--success"
            : refreshOutcome?.status === REFRESH_OUTCOME_STATUS.ERROR
              ? "plans-refresh-modal__text plans-refresh-modal__text--error"
              : "plans-refresh-modal__text";

    return (
        <div className="plans-page">
            <TopBar variant="list">
                <TopBar.Search
                    value={searchCriteria}
                    onChange={setSearchCriteria}
                    placeholder="Search clients"
                    ariaLabel="Search clients"
                    disabled={isRefreshInProgress}
                />
                <RadioButtonControlGroup
                    role="tablist"
                    ariaLabel="Benefit plan platform"
                    value={activeTab}
                    onChange={handleTabChange}
                    options={PLANS_TAB_OPTIONS}
                    minCh="18ch"
                    disabled={isRefreshInProgress}
                />
                <TopBar.Actions>
                    <button
                        type="button"
                        className="outline-btn plan-manager-top-bar__refresh"
                        onClick={handleRefresh}
                        disabled={isRefreshInProgress}
                    >
                        <LuRefreshCcw aria-hidden size={16} />
                        {REFRESH_LABEL_BY_TAB[activeTab]}
                    </button>
                </TopBar.Actions>
            </TopBar>

            {isPlansLoading && (
                <p className="plans-page__loading" role="status">
                    Loading plans...
                </p>
            )}
            {isPlansError && (
                <p className="plans-page__error" role="alert">
                    {plansErrorMessage}
                </p>
            )}
            <MainContent
                plans={plansForTable}
                shouldSliceHealthEdgePlanId={activeTab === PlanPlatformTab.HRP}
                selectedClientName={selectedClientName}
                onStartOrEditPlan={handleStartOrEditPlan}
                onExportPlan={handleExportPlan}
                onImportPlan={handleImportPlan}
                onStartOrEditNotification={handleStartOrEditNotification}
                onSelectClient={handleSelectClient}
            />

            <input
                ref={importFileInputRef}
                type="file"
                accept=".json"
                className="plans-page__hidden-file-input"
                aria-hidden
                tabIndex={-1}
                onChange={handleImportFileSelected}
            />

            <Modal
                isOpen={isImportOverwriteModalOpen}
                onClose={handleOverwriteCancel}
            >
                <div className="plans-import-modal">
                    <p className="plans-import-modal__title">
                        {IMPORT_MODAL_COPY.OVERWRITE.TITLE}
                    </p>
                    <p className="plans-import-modal__text">
                        {IMPORT_MODAL_COPY.OVERWRITE.BODY}
                    </p>
                    <div className="plans-import-modal__actions">
                        <label className="btn plans-import-modal__file-label">
                            {IMPORT_MODAL_COPY.OVERWRITE.CONFIRM}
                            <input
                                type="file"
                                accept=".json"
                                className="plans-page__hidden-file-input"
                                onChange={handleOverwriteFileSelected}
                            />
                        </label>
                        <button
                            type="button"
                            className="outline-btn"
                            onClick={handleOverwriteCancel}
                        >
                            {IMPORT_MODAL_COPY.OVERWRITE.CANCEL}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={isPlanIdMismatchModalOpen}
                onClose={handlePlanIdMismatchCancel}
            >
                <div className="plans-import-modal">
                    <p className="plans-import-modal__title">
                        {planIdMismatchDetails?.title ??
                            IMPORT_MODAL_COPY.PLAN_ID_MISMATCH.TITLE}
                    </p>
                    <p className="plans-import-modal__text">
                        {planIdMismatchDetails?.description ??
                            IMPORT_MODAL_COPY.PLAN_ID_MISMATCH.BODY}
                    </p>
                    <div className="plans-import-modal__actions">
                        <button
                            type="button"
                            className="btn"
                            onClick={handlePlanIdMismatchConfirm}
                        >
                            {IMPORT_MODAL_COPY.PLAN_ID_MISMATCH.CONFIRM}
                        </button>
                        <button
                            type="button"
                            className="outline-btn"
                            onClick={handlePlanIdMismatchCancel}
                        >
                            {IMPORT_MODAL_COPY.PLAN_ID_MISMATCH.CANCEL}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={isRefreshInProgress} onClose={handleCloseRefreshModal}>
                <div
                    className="plans-refresh-modal"
                    role="status"
                    aria-live="polite"
                    aria-busy={!refreshOutcome}
                >
                    <p className="plans-refresh-modal__title">{refreshTitle}</p>
                    <p className={refreshOutcomeClassName}>{refreshStatusText}</p>
                    {!refreshOutcome ? (
                        <>
                            <div
                                className="plans-refresh-modal__bar"
                                role="progressbar"
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-valuenow={refreshProgress.percent}
                                aria-label="Refresh progress"
                            >
                                <div
                                    className="plans-refresh-modal__bar-fill"
                                    style={{
                                        width: `${Math.max(
                                            0,
                                            Math.min(100, refreshProgress.percent),
                                        )}%`,
                                    }}
                                />
                            </div>
                            <p className="plans-refresh-modal__percent">
                                {Math.max(
                                    0,
                                    Math.min(100, refreshProgress.percent),
                                )}
                                %
                            </p>
                        </>
                    ) : null}
                </div>
            </Modal>
        </div>
    );
};

export default Plans;
